import { type GetActivityAttributeOptionsType, type GetTaskAttributeOptionsType } from '@repo/advanced-filters';
import {
  useLazyGetGraphqlDataQuery,
  type UseQueryResult,
} from '@repo/services';
import {
  FORM_FIELD_SUPPORTED_ENTITIES,
  FORM_FIELD_SUPPORTED_ENTITY_CONFIG,
  LeadSystemAttributeSchemaName,
  type SupportedEntityValueType,
} from 'crm-atoms/data';
import {
  type GetLeadAttributeOptionsType,
  getLeadSources,
  getLeadStages,
} from 'crm-atoms/queries';

type EntityResponseType<T> = T extends 'lead_attribute' ? GetLeadAttributeOptionsType :
  T extends 'activity_attribute' ? GetActivityAttributeOptionsType :
    T extends 'task_attribute' ? GetTaskAttributeOptionsType :
      never;

const getDocumentByEntityAndSchemaName = (entity: SupportedEntityValueType, schemaName: string) => {
  if (schemaName === LeadSystemAttributeSchemaName.leadStage) {
    return getLeadStages;
  } else if (schemaName === LeadSystemAttributeSchemaName.leadSource) {
    return getLeadSources;
  }
  return FORM_FIELD_SUPPORTED_ENTITY_CONFIG[entity]?.document;
};

export const useFormFieldItemOptions = <T extends SupportedEntityValueType>(
  entity: T,
  schemaName: string,
  entityId: string | number,
) => {
  const [
    fetchTrigger,
    queryResponse,
  ] = useLazyGetGraphqlDataQuery<UseQueryResult<EntityResponseType<T>>>({
    selectFromResult: (result) => {
      const refetch = 'refetch' in result ? result.refetch as () => void : () => { };

      if (!entityId || result.originalArgs?.variables?.id !== entityId) {
        return {
          ...result,
          data: undefined,
          currentData: undefined,
          refetch,
        };
      } else {
        return { ...result, refetch };
      }
    },
  });

  let parsedResponse;

  if (queryResponse.currentData) {
    if (entity === FORM_FIELD_SUPPORTED_ENTITIES.leadAttribute && 'leadAttributeOptions' in queryResponse.currentData) {
      parsedResponse = queryResponse.currentData?.leadAttributeOptions;
    } else if (
      entity === FORM_FIELD_SUPPORTED_ENTITIES.activityAttribute
      && 'activityAttributeOption' in queryResponse.currentData
    ) {
      parsedResponse = queryResponse.currentData?.activityAttributeOption;
    } else if (
      entity === FORM_FIELD_SUPPORTED_ENTITIES.taskAttribute
      && 'taskAttributeOption' in queryResponse.currentData
    ) {
      parsedResponse = queryResponse.currentData?.taskAttributeOption;
    }
  }

  return {
    queryTrigger: fetchTrigger,
    parsedResponse,
    queryResponse,
    document: getDocumentByEntityAndSchemaName(entity, schemaName),
  };
};
